<!-- SVG-based animated progress circle -->
<!-- Because of SVG limitations, it's not possible to define a linear gradient
for a circle that starts with one colour and ends on the start point
with a different colour. Instead, it always 'gradients' back to the start colour.
Because of that reason, we are using two half-circles, where we define gradients
from 0 to 180 degrees. The second circle starts with the first circle's
last gradient colour value for a smooth transition at the half.
-->

<template>
  <svg
    :key="progress"
    :aria-hidden="progress === 0 ? 'true' : null"
    :aria-labelledby="ariaLabelledById"
    :aria-valuenow="progress"
    :role="progress === 0 ? null : 'progressbar'"
    aria-valuemax="100"
    aria-valuemin="0"
    fill="none"
    viewBox="0 0 250 250"
  >
    <defs>
      <path id="circlePath" d="M236,125 A111,111 0 1,1 236,124" />
      <linearGradient id="gradientLittleProgress" x1="1" x2="0" y1="0" y2="0">
        <stop
          :stop-color="
            seasonalGradientDefs?.littleProgress?.stopColor0Pct || '#D0D4F5'
          "
        />
        <stop
          :stop-color="
            seasonalGradientDefs?.littleProgress?.stopColor15Pct || '#C2C7F3'
          "
          offset="15%"
        />
        <stop
          :stop-color="
            seasonalGradientDefs?.littleProgress?.stopColor70Pct || '#727FE0'
          "
          offset="70%"
        />
        <stop
          :stop-color="
            seasonalGradientDefs?.littleProgress?.stopColor100Pct || '#4456D6'
          "
          offset="100%"
        />
      </linearGradient>
      <linearGradient id="gradientFirstHalf" x1="1" x2="0" y1="0" y2="0">
        <stop
          :stop-color="
            seasonalGradientDefs?.firstHalf?.stopColor0Pct || '#DEE1F9'
          "
        />
        <stop
          :stop-color="
            seasonalGradientDefs?.firstHalf?.stopColor15Pct || '#C2C7F3'
          "
          offset="15%"
        />
        <stop
          :stop-color="
            seasonalGradientDefs?.firstHalf?.stopColor70Pct || '#727FE0'
          "
          offset="70%"
        />
        <stop
          :stop-color="
            seasonalGradientDefs?.firstHalf?.stopColor100Pct || '#4456D6'
          "
          offset="100%"
        />
      </linearGradient>
      <linearGradient id="gradientSecondHalf" x1="1" x2="0" y1="0" y2="0">
        <stop
          :stop-color="
            seasonalGradientDefs?.secondHalf?.stopColor0Pct || '#4456D6'
          "
          offset="0%"
        />
        <stop
          :stop-color="
            seasonalGradientDefs?.secondHalf?.stopColor100Pct || '#142ACC'
          "
          offset="100%"
        />
      </linearGradient>
      <radialGradient
        id="gradientGlow"
        cx="0"
        cy="0"
        gradientTransform="translate(125 0) scale(127)"
        gradientUnits="userSpaceOnUse"
        r="1"
      >
        <stop stop-color="#A1AAEB" />
        <stop offset="1" stop-color="#A1AAEB" stop-opacity="0" />
      </radialGradient>
    </defs>

    <!-- Base single colour circle -->
    <circle
      :pathLength="circlePathLength"
      :stroke-width="`${baseCircleWidthPx}px`"
      :style="getSeasonalStyle('progressCircleBase')"
      class="base-circle"
      cx="125"
      cy="125"
      r="111"
    />

    <g v-if="props.variant === VARIANTS.SEGMENTED">
      <circle
        id="preEtaGradientCircle"
        :pathLength="circlePathLength"
        :stroke-width="`${baseCircleWidthPx}px`"
        :transform="!isAnimationActive ? 'rotate(450 125 125)' : null"
        cx="125"
        cy="125"
        fill="none"
        r="111"
        stroke="url(#gradientGlow)"
        stroke-dasharray="100"
      >
        <animateTransform
          v-if="isAnimationActive"
          attributeName="transform"
          attributeType="XML"
          calcMode="spline"
          dur="5s"
          keySplines="0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1; 0.42 0 0.58 1"
          keyTimes="0; 0.125; 0.25; 0.375; 0.5; 0.625; 0.75; 0.875; 1"
          repeatCount="indefinite"
          type="rotate"
          values="90 125 125;
              90 125 125;
              180 125 125;
              180 125 125;
              270 125 125;
              270 125 125;
              360 125 125;
              360 125 125;
              450 125 125;"
        />
      </circle>

      <circle
        :stroke-width="`${baseCircleWidthPx + 1}px`"
        class="segmented-circle"
        cx="125"
        cy="125"
        pathLength="320"
        r="111"
        stroke-dasharray="3 17"
        transform="rotate(10 125 125)"
      />
    </g>

    <g v-if="showProgressAnimation">
      <!-- Second half of the gradient circle -->
      <!-- The second circle is rotated 180deg around its center to move
           its starting point to 6 o'clock.
           3deg gap allows the round stroke linecap -->
      <circle
        v-if="isProgressMoreThanHalf"
        :pathLength="circlePathLength"
        :stroke-dashoffset="targetDashOffset + initialDashOffset / 2"
        cx="125"
        cy="125"
        fill="none"
        r="111"
        stroke="url(#gradientSecondHalf)"
        stroke-dasharray="100"
        stroke-linecap="butt"
        stroke-width="12px"
        transform="rotate(177 125 125)"
      />

      <!-- First half of the gradient circle from 12 o'clock to 6 o'clock -->
      <circle
        :pathLength="circlePathLength"
        :stroke="`url(${isProgressMoreThanHalf ? '#gradientFirstHalf' : '#gradientLittleProgress'})`"
        :stroke-dashoffset="Math.max(targetDashOffset, initialDashOffset / 2)"
        cx="125"
        cy="125"
        fill="none"
        r="111"
        stroke-dasharray="100"
        stroke-linecap="round"
        stroke-width="12px"
      />

      <!-- Instead of animating two separate gradient circles,
           we take a reversed approach to first render the whole grey base circle
           and remove its visibility as part of the animation -->
      <circle
        :pathLength="circlePathLength"
        :stroke-dashoffset="!isAnimationActive ? progress : 0"
        :style="getSeasonalStyle('progressCircleBase')"
        class="base-circle"
        cx="125"
        cy="125"
        r="111"
        stroke-dasharray="100"
        stroke-linecap="butt"
        stroke-width="12.4px"
        transform="scale(1, -1) translate(0, -250) rotate(3, 125, 125)"
      >
        <!-- calcMode="spline" combined with keySpline values creates
             ease-in-out (acceleration) effect on the animation -->
        <animate
          v-if="isAnimationActive"
          :begin="`${animationDelay}s`"
          :dur="`${animationDuration}s`"
          :from="0"
          :to="progress"
          attributeName="stroke-dashoffset"
          calcMode="spline"
          fill="freeze"
          keySplines="0.28 0 0.72 1"
          keyTimes="0;1"
        />
      </circle>

      <!-- Car animation -->
      <!-- We use two separate animations for waiting and moving
           That way we have easier to follow parameters.
           An alternative is to use keyPoints and keyTimes combination
           in a single animateMotion but this requires a three step syntax and
           delay to totalAnimationDuration ratio calculations
           (e.g. keyTimes="`0;${delayRatio};1`", keyPoints="0;0;1") -->
      <image
        id="car"
        :href="carSrc"
        :style="getSeasonalStyle('progressCircleCar')"
      >
        <template v-if="isAnimationActive">
          <animateMotion
            id="carWaitAnimation"
            :dur="`${animationDelay}s`"
            fill="freeze"
            keyPoints="0;0"
            keyTimes="0;1"
            rotate="auto"
          >
            <mpath href="#circlePath" />
          </animateMotion>
          <animateMotion
            id="carDriveAnimation"
            :begin="`${animationDelay}s`"
            :dur="`${animationDuration}s`"
            :keyPoints="`0;${progressPercent}`"
            calcMode="spline"
            fill="freeze"
            keySplines="0.28 0 0.72 1"
            keyTimes="0;1"
            rotate="auto"
          >
            <mpath href="#circlePath" />
          </animateMotion>
        </template>
        <animateMotion
          v-else
          :keyPoints="`${progressPercent};${progressPercent}`"
          dur="0.01s"
          fill="freeze"
          keyTimes="0;1"
          rotate="auto"
        >
          <mpath href="#circlePath" />
        </animateMotion>
      </image>
    </g>
  </svg>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useSeasonalTheme } from '@/v2/composables/useSeasonalTheme.js';
import { VARIANTS } from '@/v2/components/tracking/progressCircle.constants.js';
import { useAnimation } from '@/v2/composables/useAnimation.js';

const props = defineProps({
  progress: {
    type: Number,
    validator: value => value >= 0 && value <= 100,
    default: 0
  },
  ariaLabelledById: {
    type: String,
    required: true
  },
  isGlsTenant: {
    type: Boolean,
    default: true
  },
  variant: {
    type: String,
    required: true,
    validator: value => Object.values(VARIANTS).includes(value)
  }
});

const { getSeasonalAsset, getSeasonalStyle } = useSeasonalTheme();
const { isAnimationActive, startTimedAnimation } = useAnimation();

startTimedAnimation();

watch(
  () => props.variant === VARIANTS.SEGMENTED,
  isSegmented => {
    if (isSegmented) startTimedAnimation();
  }
);

const carSrc = computed(() => {
  if (getSeasonalAsset('car')) return getSeasonalAsset('car');
  return props.isGlsTenant ? '/car-gls.svg' : '/car.svg';
});

const seasonalGradientDefs = getSeasonalStyle('progressCircleGradientDefs');

const baseCircleWidthPx = computed(() =>
  props.variant === VARIANTS.SEGMENTED ? 10 : 12
);

// When not explicitly defined, circlePathLength calculated as 2*PI*radius
// By defining the circumference as 100, we can directly associate it
// with the progress value
const circlePathLength = 100;
// dash-offset: 0 shows the full circle
// dash-offset: 100 (when circlePathLength is defined as 100) hides the circle
const initialDashOffset = circlePathLength;
const targetDashOffset = computed(() => circlePathLength - props.progress);

const showProgressAnimation = computed(
  () => props.variant === VARIANTS.PROGRESS && props.progress > 0
);
const isProgressMoreThanHalf = computed(() => props.progress > 50);

const fullCircleTourDuration = 4; // in seconds
const progressPercent = computed(() => (props.progress / 100).toFixed(2));

const animationDelay = 1;
const animationDuration = computed(() => {
  return props.progress === 0
    ? 0
    : Math.max(1, progressPercent.value * fullCircleTourDuration);
});
</script>

<style scoped>
svg {
  width: 250px;
  height: 250px;
  transform: rotate(-90deg);
  position: absolute;
  top: 0;
  left: 0;
}

.base-circle {
  stroke: #f2f2f3;
}

.segmented-circle {
  stroke: #ffffff;
}

#car {
  width: 24px;
  transform: translate(-22px, 14px) rotate(-90deg);
}
</style>
